import { AuthShowcase } from "../components/AuthShowcase";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePublicCoachingsQuery, usePublicSectionsQuery, useRegisterStudentMutation } from "../services/api";
import { deriveTenantSlug, SUPER_ADMIN_SLUG, useTenantBootstrap } from "../hooks/useTenant";
import { useAppSelector } from "../app/hooks";
import { logFrontendError } from "../services/errorReporter";
import { RequiredMark } from "../components/RequiredMark";

/**
 * Student self-serve registration flow under each tenant’s branding.
 * Why: lets learners onboard without ops help while keeping tenant context intact.
 */
export const RegisterPage = () => {
    useTenantBootstrap();
    const hostname = typeof window !== "undefined" ? window.location.hostname : SUPER_ADMIN_SLUG;
    const tenantSlug = useAppSelector((state) => state.tenant.slug) ?? deriveTenantSlug(hostname);
    const tenantBranding = useAppSelector((state) => state.tenant.branding);
    const isPlatformTenant = tenantSlug === SUPER_ADMIN_SLUG;
    // A real coaching's name is a welcome sign that you are in the right
    // place. The platform tenant's own name is not — it resolves to the
    // super-admin org on any non-subdomain host, so every student reaching
    // the plain login URL was greeted with internal naming.
    const brandName = isPlatformTenant ? "Green Agentick" : (tenantBranding?.display_name ?? "Green Agentick");

    const [formState, setFormState] = useState({
        fullName: "",
        email: "",
        password: ""
    });
    const [registerStudent, { isLoading, error }] = useRegisterStudentMutation();
    // Every sign-up now waits for the institute's approval (plan 5e), so the
    // success line names whom it waits on instead of inviting a sign-in.
    const [submittedTo, setSubmittedTo] = useState<string | null>(null);
    const { data: coachings, isLoading: coachingsLoading, isError: coachingsError } = usePublicCoachingsQuery();
    const [selectedCoachingId, setSelectedCoachingId] = useState<string>("");
    // Arriving from a friend's challenge link: the coaching is the one the
    // challenge belongs to, chosen for the student and shown locked, because
    // joining any other coaching would leave them unable to take it.
    const [search] = useSearchParams();
    const inviteCoaching = search.get("invite") ? search.get("coaching") || "" : "";
    const inviteMatch = (coachings ?? []).find((coaching) => coaching.coaching_id === inviteCoaching);
    const selectedOrg = (coachings ?? []).find((coaching) => coaching.coaching_id === selectedCoachingId);
    const isSchool = selectedOrg?.org_type === "school";
    const { data: sections, isFetching: sectionsLoading } = usePublicSectionsQuery(selectedCoachingId, { skip: !isSchool });
    const [sectionId, setSectionId] = useState("");
    // A class belongs to one school: changing the school clears it.
    useEffect(() => setSectionId(""), [selectedCoachingId]);
    useEffect(() => {
        if (inviteMatch && selectedCoachingId !== inviteMatch.coaching_id) setSelectedCoachingId(inviteMatch.coaching_id);
    }, [inviteMatch, selectedCoachingId]);

    // Why: the hostname-derived tenant slug is only a *hint* — on shared/local
    // domains it resolves to the platform pseudo-tenant (SUPER_ADMIN_SLUG),
    // which is never a real coaching. Pre-select it only when it actually
    // matches an active coaching from the public directory; otherwise force
    // the student to explicitly choose, so every registration maps to a real,
    // active coaching and the "students only see their own coaching" isolation
    // model can never be silently broken by a bad/guessed slug.
    useEffect(() => {
        if (!coachings || coachings.length === 0 || selectedCoachingId) {
            return;
        }
        const hostMatch = coachings.find((coaching) => coaching.coaching_id === tenantSlug);
        setSelectedCoachingId(hostMatch ? hostMatch.coaching_id : "");
    }, [coachings, tenantSlug, selectedCoachingId]);

    // Only while sending. Greying the button out until every field was filled
    // meant a click that did nothing and never said WHICH field was missing.
    // Enabled, the form's own `required` / `type="email"` / `minLength` rules
    // point at the exact field with a message about that field.
    const isDisabled = isLoading;

    /**
     * Submits the registration payload through RTK Query.
     * Why: centralizes validation and error messaging for student signup.
     */
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedCoachingId || (isSchool && !sectionId)) {
            return;
        }
        const payload = {
            email: formState.email,
            password: formState.password,
            full_name: formState.fullName,
            coaching_id: selectedCoachingId,
            // The real tenant is the platform-wide slug (matches every other seeded
            // account) — coaching_id is the sub-scope, not a tenant of its own. Sending
            // coaching_id as tenant_slug here used to put new registrations in a
            // different tenant scope than everyone else, so the backend's per-tenant
            // duplicate-email check never found existing accounts and silently created
            // unusable duplicates.
            tenant_slug: tenantSlug,
            ...(isSchool ? { class_section_id: sectionId } : {})
        };
        try {
            await registerStudent(payload).unwrap();
            setSubmittedTo(selectedOrg?.name ?? "your institute");
            setFormState({ fullName: "", email: "", password: "" });
        } catch (registrationError) {
            await logFrontendError("RegisterPage.handleSubmit", "Student registration failed", registrationError, {
                coachingId: selectedCoachingId,
                email: formState.email
            });
        }
    };

    const friendlyError = useMemo(() => {
        if (!error) {
            return null;
        }
        if ("status" in error && error.status === 409) {
            return "An account already exists with this email.";
        }
        if ("status" in error && (error.status === 404 || error.status === 400)) {
            const data = (error as { data?: { detail?: string } }).data;
            return data?.detail ?? "The selected institute is unavailable. Please choose another.";
        }
        return "We couldn’t complete your registration. Please verify the details and try again.";
    }, [error]);

    const passwordScore = useMemo(() => {
        if (!formState.password) {
            return 0;
        }
        if (formState.password.length >= 14) {
            return 3;
        }
        if (formState.password.length >= 10) {
            return 2;
        }
        return 1;
    }, [formState.password]);

    const passwordLabel = ["Add a password", "Weak", "Good", "Strong"][passwordScore] ?? "Weak";

    return (
        <div className="login-shell">
            <section className="login-hero">
                <Link to="/" className="login-hero__brand">{brandName}</Link>
                <div className="login-hero__center">
                    <p className="login-hero__eyebrow">students only · free account</p>
                    <h1>Your AI study partner<br />starts here.</h1>
                    <AuthShowcase variant="register" />
                </div>
                <p className="login-hero__foot">© {new Date().getFullYear()} {brandName}</p>
            </section>

            <section className="login-panel">
                <form className="login-card login-card--register" onSubmit={handleSubmit} aria-live="polite">
                    <div className="login-card__header">
                        {/* Phones hide the ink hero panel; keep the brand on the card. */}
                        <Link to="/" className="login-card__brand">{brandName}</Link>
                        <h2 className="login-card__title">Create your student account</h2>
                        <p className="auth-card__description">
                            Already registered? <Link to="/auth">Sign in</Link>
                        </p>
                    </div>
                    <div className="auth-form">
                        <label className="form-field">
                            <span className="form-label">Full name <RequiredMark /></span>
                            <input
                                className="text-input"
                                value={formState.fullName}
                                onChange={(event) => setFormState((prev) => ({ ...prev, fullName: event.target.value }))}
                                placeholder="Ranveer Sharma"
                                autoComplete="name"
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span className="form-label">Email <RequiredMark /></span>
                            <input
                                className="text-input"
                                value={formState.email}
                                onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
                                placeholder="you@example.com"
                                autoComplete="email"
                                type="email"
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span className="form-label">Password <RequiredMark /></span>
                            <input
                                className="text-input"
                                value={formState.password}
                                onChange={(event) => setFormState((prev) => ({ ...prev, password: event.target.value }))}
                                placeholder="Create a strong password"
                                type="password"
                                autoComplete="new-password"
                                minLength={8}
                                required
                            />
                            <div className="password-meter" aria-live="polite">
                                <div className="password-meter__bar" data-score={passwordScore} />
                                <span className="password-meter__label">{passwordLabel}</span>
                            </div>
                        </label>
                        <label className="form-field">
                            <span className="form-label">Coaching or school <RequiredMark /></span>
                            <select
                                className="text-input"
                                value={selectedCoachingId}
                                onChange={(event) => setSelectedCoachingId(event.target.value)}
                                required
                                disabled={coachingsLoading || Boolean(inviteMatch)}
                            >
                                <option value="">
                                    {coachingsLoading ? "Loading…" : "Select your coaching or school"}
                                </option>
                                {(coachings ?? []).map((coaching) => (
                                    <option key={coaching.coaching_id} value={coaching.coaching_id}>
                                        {coaching.org_type === "school" ? `${coaching.name} (school)` : coaching.name}
                                    </option>
                                ))}
                            </select>
                            <span className="form-hint">
                                {coachingsError
                                    ? "Unable to load the coaching directory. Please refresh and try again."
                                    : inviteMatch
                                      ? `Chosen for you: the challenge you were sent is from ${inviteMatch.name}.`
                                      : "Pick the coaching or school that invited you."}
                            </span>
                        </label>
                        {isSchool && (
                            <label className="form-field">
                                <span className="form-label">Class and section <RequiredMark /></span>
                                <select
                                    className="text-input"
                                    value={sectionId}
                                    onChange={(event) => setSectionId(event.target.value)}
                                    required
                                    disabled={sectionsLoading}
                                >
                                    <option value="">{sectionsLoading ? "Loading classes…" : "Select your class"}</option>
                                    {(sections ?? []).map((section) => (
                                        <option key={section.id} value={section.id}>
                                            Class {section.grade}-{section.section}
                                        </option>
                                    ))}
                                </select>
                                <span className="form-hint">
                                    {!sectionsLoading && (sections ?? []).length === 0
                                        ? "This school has not added its classes yet. Ask your school."
                                        : "Your class teacher or school will approve you."}
                                </span>
                            </label>
                        )}
                        <div className="auth-form__actions">
                            <button type="submit" className="btn btn-primary" disabled={isDisabled}>
                                {isLoading ? "Creating account…" : "Create free account"}
                            </button>
                            <p className="form-hint">
                                Student sign-up only. Teachers &amp; institutes — please <a href="mailto:support@greenagentick.com">contact support</a> for access.
                            </p>
                        </div>
                        {submittedTo && (
                            <p className="form-success" role="status">
                                Sent to {submittedTo} for approval. You can <Link to="/auth">sign in</Link> once they approve it.
                            </p>
                        )}
                        {friendlyError && (
                            <p className="form-error" role="alert">
                                {friendlyError}
                            </p>
                        )}
                    </div>
                </form>
            </section>
        </div>
    );
};
